import {
    Plus,
    GitBranch,
    Link2,
    Pencil,
    Copy,
    Trash2,
    MoreVertical,
} from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const VIEWPORT_GAP = 12;

const MENU_ITEMS = [
    {
        key: "add",
        label: "Add next action",
        icon: Plus,
        danger: false,
    },
    {
        key: "branch",
        label: "Add decision path",
        icon: GitBranch,
        danger: false,
    },
    {
        key: "connect",
        label: "Connect existing action",
        icon: Link2,
        danger: false,
    },
    {
        key: "edit",
        label: "Edit action",
        icon: Pencil,
        danger: false,
    },
    {
        key: "duplicate",
        label: "Duplicate action",
        icon: Copy,
        danger: false,
    },
    {
        key: "delete",
        label: "Delete action",
        icon: Trash2,
        danger: true,
    },
];

export default function NodeContextMenu({
    node,
    onAdd,
    onBranch,
    onConnect,
    onEdit,
    onDuplicate,
    onDelete,
}) {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const triggerRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;

        function handleClick(e) {
            if (!menuRef.current?.contains(e.target) && !triggerRef.current?.contains(e.target)) {
                setOpen(false);
            }
        }

        function closeMenu() {
            setOpen(false);
        }

        function handleKeyDown(event) {
            if (event.key === "Escape") closeMenu();
        }

        window.addEventListener("mousedown", handleClick);
        window.addEventListener("resize", closeMenu);
        window.addEventListener("scroll", closeMenu, true);
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("mousedown", handleClick);
            window.removeEventListener("resize", closeMenu);
            window.removeEventListener("scroll", closeMenu, true);
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [open]);

    useLayoutEffect(() => {
        if (!open || !triggerRef.current || !menuRef.current) return;

        const triggerRect = triggerRef.current.getBoundingClientRect();
        const menuRect = menuRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const preferredLeft = triggerRect.right - menuRect.width;
        const left = Math.min(
            Math.max(VIEWPORT_GAP, preferredLeft),
            Math.max(VIEWPORT_GAP, viewportWidth - menuRect.width - VIEWPORT_GAP)
        );
        const roomBelow = viewportHeight - triggerRect.bottom;
        const top = roomBelow >= menuRect.height + VIEWPORT_GAP
            ? triggerRect.bottom + 8
            : Math.max(VIEWPORT_GAP, triggerRect.top - menuRect.height - 8);

        setPosition({ top, left });
    }, [open]);

    const handleAction = (key) => {
        setOpen(false);

        switch (key) {
            case "add":
                onAdd?.(node);
                break;

            case "branch":
                onBranch?.(node);
                break;

            case "connect":
                onConnect?.(node);
                break;

            case "edit":
                onEdit?.(node);
                break;

            case "duplicate":
                onDuplicate?.(node);
                break;

            case "delete":
                onDelete?.(node);
                break;

            default:
                break;
        }
    };

    const menu = open ? createPortal(
        <div
            ref={menuRef}
            role="menu"
            aria-label={`Actions for ${node?.name || "workflow action"}`}
            style={{ top: position.top, left: position.left }}
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
            className="fixed z-[10000] w-56 overflow-hidden rounded-2xl border border-[#dce7f1] bg-white py-1.5 shadow-[0_18px_45px_rgba(31,78,121,0.22)]"
        >
            {MENU_ITEMS.map((item) => {
                const Icon = item.icon;

                return (
                    <button
                        type="button"
                        role="menuitem"
                        key={item.key}
                        onClick={() => handleAction(item.key)}
                        className={`flex w-full items-center gap-3 px-3.5 py-2.5 text-left text-xs font-semibold transition
                        ${item.danger
                            ? "text-red-600 hover:bg-red-50"
                            : "text-[#3f5878] hover:bg-[#f1f8fc] hover:text-[#147db5]"
                        }`}
                    >
                        <Icon size={17} />
                        <span>{item.label}</span>
                    </button>
                );
            })}
        </div>,
        document.body
    ) : null;

    return (
        <div className="relative">
            <button
                ref={triggerRef}
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((v) => !v);
                }}
                aria-label={`Actions for ${node?.name || "workflow action"}`}
                title="Action menu"
                className="rounded-lg p-1.5 text-[#8293aa] transition hover:bg-[#f0f6fa] hover:text-[#147db5]"
            >
                <MoreVertical size={18} />
            </button>
            {menu}
        </div>
    );
}
